/**
 * WebGL2 instanced tile layer: one triangulated outline per leaf type, drawn
 * `drawElementsInstanced` with each tile's affine transform as instance
 * attributes, plus a per-tile tint (the owner's colour on claimed tiles).
 * A quarter of a million tiles is ~10 draw calls and no per-frame JS work,
 * which is what a level-6 arena needs. Strands are drawn by the Canvas2D
 * overlay on top; this layer is tiles only.
 *
 * The Spectre view of a hexagon board (`spectre-view.ts`) is a second set of
 * batches, one per Spectre leaf type, whose instances carry two transforms —
 * the piece's own and its hex parent's — and whose vertices carry two
 * positions — the Spectre's and its home in the hexagon. The shader mixes
 * the two by `u_t`, so the morph between the shapes is the same single
 * instanced pass, and the plain hexagon batches are the degenerate case
 * (both transforms the tile's, both positions the vertex's, `u_t` 0).
 */

import type { Field } from '../../shared/game/field';
import { leafPts, type Pt } from '../../shared/tiles';
import type { Camera } from './camera';
import { VIEW_FAMILY, hexTypeOf, type SpectreView } from './spectre-view';
import type { BoardTheme } from './theme';
import { ARROW_MIN_SCALE, directionArrow, type Rgb01, type TileLayer } from './tiles-layer';

const VS = `#version 300 es
precision highp float;
in vec2 a_local;
in vec2 a_home;
in vec3 a_m0;
in vec3 a_m1;
in vec3 a_h0;
in vec3 a_h1;
in vec4 a_tint;
uniform vec2 u_cam;
uniform float u_scale;
uniform vec2 u_half;
uniform float u_t;
out vec4 v_tint;
void main() {
  vec2 ws = vec2(dot(a_m0.xy, a_local) + a_m0.z, dot(a_m1.xy, a_local) + a_m1.z);
  vec2 wh = vec2(dot(a_h0.xy, a_home) + a_h0.z, dot(a_h1.xy, a_home) + a_h1.z);
  vec2 w = mix(wh, ws, u_t);
  vec2 s = (w - u_cam) * u_scale;
  gl_Position = vec4(s.x / u_half.x, -s.y / u_half.y, 0.0, 1.0);
  v_tint = a_tint;
}`;

const FS = `#version 300 es
precision mediump float;
in vec4 v_tint;
uniform vec4 u_fill;
uniform float u_useTint;
out vec4 o;
void main() {
  vec3 rgb = mix(u_fill.rgb, v_tint.rgb, v_tint.a * u_useTint);
  o = vec4(rgb, u_fill.a);
}`;

/** Ear-clipping triangulation of a simple polygon (n ≤ 14 here). */
function triangulate(pts: readonly Pt[]): number[] {
  const n = pts.length;
  const idx: number[] = [];
  for (let i = 0; i < n; i++) idx.push(i);
  let area = 0;
  for (let i = 0; i < n; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % n];
    area += a.x * b.y - b.x * a.y;
  }
  if (area < 0) idx.reverse();
  const cross = (o: Pt, a: Pt, b: Pt): number => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  const inside = (p: Pt, a: Pt, b: Pt, c: Pt): boolean =>
    cross(a, b, p) >= -1e-12 && cross(b, c, p) >= -1e-12 && cross(c, a, p) >= -1e-12;
  const out: number[] = [];
  let guard = 0;
  while (idx.length > 3 && guard++ < 1000) {
    let clipped = false;
    for (let i = 0; i < idx.length; i++) {
      const i0 = idx[(i + idx.length - 1) % idx.length];
      const i1 = idx[i];
      const i2 = idx[(i + 1) % idx.length];
      const a = pts[i0];
      const b = pts[i1];
      const c = pts[i2];
      if (cross(a, b, c) <= 1e-12) continue; // reflex
      let ok = true;
      for (const j of idx) {
        if (j === i0 || j === i1 || j === i2) continue;
        if (inside(pts[j], a, b, c)) {
          ok = false;
          break;
        }
      }
      if (!ok) continue;
      out.push(i0, i1, i2);
      idx.splice(i, 1);
      clipped = true;
      break;
    }
    if (!clipped) break;
  }
  if (idx.length === 3) out.push(idx[0], idx[1], idx[2]);
  return out;
}

function compile(gl: WebGL2RenderingContext, type: number, src: string): WebGLShader {
  const sh = gl.createShader(type)!;
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(sh) ?? 'shader');
  return sh;
}

interface TypeBatch {
  readonly vao: WebGLVertexArrayObject;
  readonly indexCount: number;
  readonly vertexCount: number;
  readonly first: number;
  readonly count: number;
  /** Index into the fills array (a hex leaf type), which the theme can swap out under us. */
  readonly fill: number;
}

/** A set of batches over one instance buffer: the hexagons, or the Spectre view's pieces. */
interface Geometry {
  readonly batches: readonly TypeBatch[];
  readonly instBuf: WebGLBuffer;
  readonly tintBuf: WebGLBuffer;
  readonly tints: Uint8Array;
  /** Instance → sorted slot. */
  readonly slotOf: Int32Array;
  readonly buffers: readonly WebGLBuffer[];
  readonly vaos: readonly WebGLVertexArrayObject[];
  tintsDirty: boolean;
}

/**
 * Software GL (SwiftShader, llvmpipe, Mesa's softpipe) rasterises a quarter
 * of a million instances in seconds per frame — worse than Canvas2D, whose
 * software path is well optimised. Prefer the 2D layer there unless forced.
 */
export function isSoftwareRenderer(gl: WebGL2RenderingContext): boolean {
  const ext = gl.getExtension('WEBGL_debug_renderer_info');
  const renderer = String(ext ? gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER));
  return /swiftshader|llvmpipe|softpipe|software|mesa offscreen/i.test(renderer);
}

export interface GlOptions {
  /** `true` forces WebGL even on a software renderer; `false` forbids it. */
  readonly force?: boolean;
}

/**
 * Probe on a throwaway canvas: a canvas that has ever handed out a WebGL
 * context can never hand out a 2D one, so the real canvas must stay untouched
 * until we know we are keeping WebGL.
 */
function webglUsable(force: boolean | undefined): boolean {
  if (force === false) return false;
  const probe = document.createElement('canvas');
  const gl = probe.getContext('webgl2');
  if (!gl) return false;
  const ok = force === true || !isSoftwareRenderer(gl);
  gl.getExtension('WEBGL_lose_context')?.loseContext();
  return ok;
}

export function createGlTiles(
  canvas: HTMLCanvasElement,
  field: Field,
  fills: readonly Rgb01[],
  board: BoardTheme,
  opts: GlOptions = {},
): TileLayer | null {
  if (!webglUsable(opts.force)) return null;
  const gl = canvas.getContext('webgl2', { antialias: true, alpha: false, premultipliedAlpha: false });
  if (!gl) return null;

  const prog = gl.createProgram()!;
  gl.attachShader(prog, compile(gl, gl.VERTEX_SHADER, VS));
  gl.attachShader(prog, compile(gl, gl.FRAGMENT_SHADER, FS));
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(prog) ?? 'link');
  const loc = {
    local: gl.getAttribLocation(prog, 'a_local'),
    home: gl.getAttribLocation(prog, 'a_home'),
    m0: gl.getAttribLocation(prog, 'a_m0'),
    m1: gl.getAttribLocation(prog, 'a_m1'),
    h0: gl.getAttribLocation(prog, 'a_h0'),
    h1: gl.getAttribLocation(prog, 'a_h1'),
    tint: gl.getAttribLocation(prog, 'a_tint'),
    cam: gl.getUniformLocation(prog, 'u_cam'),
    scale: gl.getUniformLocation(prog, 'u_scale'),
    half: gl.getUniformLocation(prog, 'u_half'),
    t: gl.getUniformLocation(prog, 'u_t'),
    fill: gl.getUniformLocation(prog, 'u_fill'),
    useTint: gl.getUniformLocation(prog, 'u_useTint'),
  };

  /**
   * The vertex attributes of one shape: `verts` holds `[local, home]` pairs
   * (`home` the same as `local` for a plain tile); the instance buffer holds
   * `[m0, m1, h0, h1]` per instance (12 floats), `h` the same as `m` for a
   * plain tile. One VAO per shape, its instances a contiguous run.
   */
  const shapeVao = (
    verts: Float32Array,
    tri: Uint16Array | null,
    instBuf: WebGLBuffer,
    firstSlot: number,
    tintBuf: WebGLBuffer | null,
    buffers: WebGLBuffer[],
  ): WebGLVertexArrayObject => {
    const vao = gl.createVertexArray()!;
    gl.bindVertexArray(vao);
    const vbo = gl.createBuffer()!;
    buffers.push(vbo);
    gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
    gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
    gl.enableVertexAttribArray(loc.local);
    gl.vertexAttribPointer(loc.local, 2, gl.FLOAT, false, 16, 0);
    gl.enableVertexAttribArray(loc.home);
    gl.vertexAttribPointer(loc.home, 2, gl.FLOAT, false, 16, 8);
    if (tri) {
      const ebo = gl.createBuffer()!;
      buffers.push(ebo);
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ebo);
      gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, tri, gl.STATIC_DRAW);
    }
    gl.bindBuffer(gl.ARRAY_BUFFER, instBuf);
    const base = firstSlot * 48;
    for (const [l, off] of [
      [loc.m0, 0],
      [loc.m1, 12],
      [loc.h0, 24],
      [loc.h1, 36],
    ] as const) {
      gl.enableVertexAttribArray(l);
      gl.vertexAttribPointer(l, 3, gl.FLOAT, false, 48, base + off);
      gl.vertexAttribDivisor(l, 1);
    }
    if (tintBuf) {
      gl.bindBuffer(gl.ARRAY_BUFFER, tintBuf);
      gl.enableVertexAttribArray(loc.tint);
      gl.vertexAttribPointer(loc.tint, 4, gl.UNSIGNED_BYTE, true, 4, firstSlot * 4);
      gl.vertexAttribDivisor(loc.tint, 1);
    } else {
      // No per-instance tint (the pass draws with u_useTint = 0).
      gl.disableVertexAttribArray(loc.tint);
      gl.vertexAttrib4f(loc.tint, 0, 0, 0, 0);
    }
    gl.bindVertexArray(null);
    return vao;
  };

  /**
   * Instances sorted by type so each type is one contiguous instanced draw.
   * `xformOf(i)` fills 12 floats: the instance's transform, then the one its
   * vertices' homes are measured in.
   */
  const geometry = (
    n: number,
    typeOf: (i: number) => number,
    types: number,
    shapeOf: (type: number) => { local: readonly Pt[]; home: readonly Pt[]; fill: number },
    xformOf: (i: number, out: Float32Array, o: number) => void,
  ): Geometry => {
    const perType: number[] = new Array(types).fill(0);
    for (let i = 0; i < n; i++) perType[typeOf(i)]++;
    const firstOf: number[] = [];
    let acc = 0;
    for (let t = 0; t < types; t++) {
      firstOf.push(acc);
      acc += perType[t];
    }
    const slotOf = new Int32Array(n);
    const cursor = [...firstOf];
    const inst = new Float32Array(n * 12);
    for (let i = 0; i < n; i++) {
      const s = cursor[typeOf(i)]++;
      slotOf[i] = s;
      xformOf(i, inst, s * 12);
    }
    const tints = new Uint8Array(n * 4);
    const instBuf = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, instBuf);
    gl.bufferData(gl.ARRAY_BUFFER, inst, gl.STATIC_DRAW);
    const tintBuf = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, tintBuf);
    gl.bufferData(gl.ARRAY_BUFFER, tints, gl.DYNAMIC_DRAW);
    const buffers: WebGLBuffer[] = [instBuf, tintBuf];
    const vaos: WebGLVertexArrayObject[] = [];
    const batches: TypeBatch[] = [];
    for (let t = 0; t < types; t++) {
      if (perType[t] === 0) continue;
      const { local, home, fill } = shapeOf(t);
      const verts = new Float32Array(local.length * 4);
      local.forEach((p, i) => {
        verts[i * 4] = p.x;
        verts[i * 4 + 1] = p.y;
        verts[i * 4 + 2] = home[i].x;
        verts[i * 4 + 3] = home[i].y;
      });
      const tri = new Uint16Array(triangulate(local));
      const vao = shapeVao(verts, tri, instBuf, firstOf[t], tintBuf, buffers);
      vaos.push(vao);
      batches.push({ vao, indexCount: tri.length, vertexCount: local.length, first: firstOf[t], count: perType[t], fill });
    }
    return { batches, instBuf, tintBuf, tints, slotOf, buffers, vaos, tintsDirty: false };
  };

  const n = field.count;
  const hex = geometry(
    n,
    (i) => field.types[i],
    field.leafTypes.length,
    (t) => {
      const pts = leafPts(field.family, field.leafTypes[t]);
      return { local: pts, home: pts, fill: t };
    },
    (i, out, o) => {
      for (let k = 0; k < 6; k++) {
        out[o + k] = field.xforms[i * 6 + k];
        out[o + 6 + k] = field.xforms[i * 6 + k];
      }
    },
  );

  // The direction arrow: one shape in tile-local coordinates for every tile of
  // the family, so the whole field is a single instanced draw over the hex
  // instance buffer. Only hexagons need it — a Spectre's outline already
  // shows its rotation.
  let arrowVao: WebGLVertexArrayObject | null = null;
  let arrowIndexCount = 0;
  if (field.family === 'hex') {
    const pts = directionArrow(leafPts(field.family, field.leafTypes[0]));
    const verts = new Float32Array(pts.length * 4);
    pts.forEach((p, i) => {
      verts[i * 4] = p.x;
      verts[i * 4 + 1] = p.y;
      verts[i * 4 + 2] = p.x;
      verts[i * 4 + 3] = p.y;
    });
    const tri = new Uint16Array(triangulate(pts));
    arrowIndexCount = tri.length;
    arrowVao = shapeVao(verts, tri, hex.instBuf, 0, null, hex.buffers as WebGLBuffer[]);
  }

  gl.enable(gl.BLEND);
  gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
  let typeFills = fills;
  let scheme = board;
  let arrows = true;
  /** The Spectre view's pieces, built when the view arrives. */
  let view: SpectreView | null = null;
  let pieces: Geometry | null = null;
  let morph = 0;

  const dropPieces = (): void => {
    if (!pieces) return;
    for (const vao of pieces.vaos) gl.deleteVertexArray(vao);
    for (const b of pieces.buffers) gl.deleteBuffer(b);
    pieces = null;
  };

  const tintPiece = (tile: number, r: number, g: number, b: number, a: number): void => {
    if (!view || !pieces) return;
    for (let s = view.first[tile]; s < view.first[tile + 1]; s++) {
      const q = pieces.slotOf[s] * 4;
      pieces.tints[q] = r;
      pieces.tints[q + 1] = g;
      pieces.tints[q + 2] = b;
      pieces.tints[q + 3] = a;
    }
    pieces.tintsDirty = true;
  };

  const upload = (g: Geometry): void => {
    if (!g.tintsDirty) return;
    gl.bindBuffer(gl.ARRAY_BUFFER, g.tintBuf);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, g.tints);
    g.tintsDirty = false;
  };

  return {
    kind: 'webgl',
    resize(pw, ph) {
      if (canvas.width !== pw || canvas.height !== ph) {
        canvas.width = pw;
        canvas.height = ph;
      }
    },
    setArrows(on) {
      arrows = on;
    },
    setTheme(next, nextFills) {
      scheme = next;
      typeFills = nextFills;
    },
    setView(next) {
      if (next === view) return;
      dropPieces();
      view = next;
      if (!view) return;
      const v = view;
      pieces = geometry(
        v.count,
        (s) => v.types[s],
        v.leafTypes.length,
        // A piece wears its hex tile's colour: both halves of the Mystic the Gamma's.
        (t) => ({ local: leafPts(VIEW_FAMILY, v.leafTypes[t]), home: v.homes[t], fill: field.leafTypes.indexOf(hexTypeOf(v.leafTypes[t])) }),
        (s, out, o) => {
          const h = v.parent[s] * 6;
          for (let k = 0; k < 6; k++) {
            out[o + k] = v.xforms[s * 6 + k];
            out[o + 6 + k] = field.xforms[h + k];
          }
        },
      );
      // The pieces take the tints their tiles already wear.
      for (let i = 0; i < n; i++) {
        const q = hex.slotOf[i] * 4;
        if (hex.tints[q + 3] !== 0) tintPiece(i, hex.tints[q], hex.tints[q + 1], hex.tints[q + 2], hex.tints[q + 3]);
      }
    },
    setMorph(t) {
      morph = Math.max(0, Math.min(1, t));
    },
    clearTints() {
      hex.tints.fill(0);
      hex.tintsDirty = true;
      if (pieces) {
        pieces.tints.fill(0);
        pieces.tintsDirty = true;
      }
    },
    setTint(tile, r, g, b, a) {
      const s = hex.slotOf[tile] * 4;
      hex.tints[s] = r;
      hex.tints[s + 1] = g;
      hex.tints[s + 2] = b;
      hex.tints[s + 3] = a;
      hex.tintsDirty = true;
      tintPiece(tile, r, g, b, a);
    },
    draw(cam: Camera, width: number, height: number, dpr: number) {
      // The hexagons themselves until the morph starts; the pieces from then on.
      const morphing = pieces !== null && morph > 0;
      const g = morphing ? pieces! : hex;
      upload(g);
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.clearColor(scheme.bg[0], scheme.bg[1], scheme.bg[2], 1);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.useProgram(prog);
      gl.uniform2f(loc.cam, cam.x, cam.y);
      gl.uniform1f(loc.scale, cam.scale * dpr);
      gl.uniform2f(loc.half, (width * dpr) / 2, (height * dpr) / 2);
      gl.uniform1f(loc.t, morphing ? morph : 0);
      const outlines = cam.scale > 4;
      for (const b of g.batches) {
        gl.bindVertexArray(b.vao);
        const c = typeFills[b.fill];
        gl.uniform4f(loc.fill, c[0], c[1], c[2], 1);
        gl.uniform1f(loc.useTint, 1);
        gl.drawElementsInstanced(gl.TRIANGLES, b.indexCount, gl.UNSIGNED_SHORT, 0, b.count);
        if (outlines) {
          gl.uniform4f(loc.fill, scheme.ink[0], scheme.ink[1], scheme.ink[2], scheme.lineAlpha);
          gl.uniform1f(loc.useTint, 0);
          gl.drawArraysInstanced(gl.LINE_LOOP, 0, b.vertexCount, b.count);
        }
      }
      // Arrows last, so a claimed tile keeps its direction — on the hexagons only.
      if (arrowVao && arrows && !morphing && cam.scale > ARROW_MIN_SCALE) {
        gl.bindVertexArray(arrowVao);
        gl.uniform4f(loc.fill, scheme.ink[0], scheme.ink[1], scheme.ink[2], scheme.arrowAlpha);
        gl.uniform1f(loc.useTint, 0);
        gl.drawElementsInstanced(gl.TRIANGLES, arrowIndexCount, gl.UNSIGNED_SHORT, 0, n);
      }
      gl.bindVertexArray(null);
    },
    dispose() {
      dropPieces();
      gl.getExtension('WEBGL_lose_context')?.loseContext();
    },
  };
}
